// @flow
import * as React from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/statistic.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/message.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/table.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/label.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/icon.css'
import './styles.scss'
import PollingStrip from 'components/pollingStrip'
import config from 'config'

type Props = {
  angebot_gem: number,
  canton?: string,
  municipalityName: string,
  offersInSizeRange: number,
  priceRangeQuotas: Array<number>,
  selectedBudgetCategory: number,
  selectedFlatSizeCategory: number,
  sizeRangeQuota: number,
  summedPriceRangeQuotas: Array<number>
}

/**
 * Small component that provides additional details on a municipality.
 *
 * Proposed use within the list on the bottom of the map and as a popup
 * information window
 *
 * @param {object} props
 */
export function MunicipalityDetail ({
  angebot_gem: offerTotal = 456,
  canton,
  municipalityName = 'Bern',
  offersInSizeRange,
  priceRangeQuotas,
  selectedBudgetCategory = 3,
  selectedFlatSizeCategory = 3,
  sizeRangeQuota = 0.66,
  summedPriceRangeQuotas
}: Props): React.Element<'div'> {
  // Anzahl Wohnungen verfügbare Wohnungen innerhalb der Grössen-Kategorie:
  let amountOfAvailableFlatsInSizeCategory = offersInSizeRange
  let anteil = summedPriceRangeQuotas[selectedBudgetCategory - 1]
  let availableFlatsWithinBudget = anteil * amountOfAvailableFlatsInSizeCategory

  let qualifier =
    Math.abs(
      anteil -
        Math.round(availableFlatsWithinBudget) /
          Math.round(amountOfAvailableFlatsInSizeCategory)
    ) >= 0.01
      ? 'etwa'
      : ''

  let explanation = (
    <p>
      In {municipalityName} liegen{' '}
      <strong>{config.formatAsPercent(anteil)}</strong> der Wohnungen mit{' '}
      {config.flatSizeCategories[+selectedFlatSizeCategory - 1][2]} innerhalb
      Ihres Budgets – das sind {qualifier}{' '}
      <strong>{Math.round(availableFlatsWithinBudget)}</strong> von{' '}
      {Math.round(amountOfAvailableFlatsInSizeCategory)}.
    </p>
  )

  if (sizeRangeQuota <= 0) {
    explanation = (
      <p>
        In {municipalityName} wurden im untersuchten Zeitraum keine Wohnungen
        dieser Grösse angeboten.
      </p>
    )
  }

  let cantonAddition = canton && canton !== '' ? ` (${canton})` : ''

  return (
    <div className='municipality-detail'>
      <div className='ui tiny infographic header'>
        {municipalityName}
        {cantonAddition}
      </div>

      <table className='stats ui very basic unstackable right aligned table'>
        <thead>
          <tr>
            <td
              className={`${offerTotal < config.notEnoughData
                ? 'warning'
                : ''}`}
            >
              Total angebotene Wohnungen
            </td>
            <td>
              Davon {config.flatSizeCategories[selectedFlatSizeCategory - 1][1]}
            </td>
            <td>Innerhalb des Budgets</td>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td
              className={`${offerTotal < config.notEnoughData
                ? 'warning'
                : ''}`}
            >
              {config.formatAsInteger(offerTotal)}
            </td>
            <td>
              {config.formatAsInteger(amountOfAvailableFlatsInSizeCategory)}
            </td>
            <td>{config.formatAsInteger(availableFlatsWithinBudget)}</td>
          </tr>
        </tbody>
      </table>

      <div>{explanation}</div>
      <PollingStrip
        priceRanges={priceRangeQuotas}
        summedPriceRanges={summedPriceRangeQuotas}
      />
    </div>
  )
}

export type { Props as MunicipalityDetailProps }
